import { useMemo, useState } from "react";
import Card from "@/components/common/Card";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import {
    AttendanceToolbar,
    EmployeeToolbar,
    SalaryCreateAction,
    SelectionSummary,
} from "../../../components/CreateSalary/CreateSalaryActions";
import { CreateSalaryFiltersBar } from "../../../components/CreateSalary/CreateSalaryFilters";
import {
    useAttendanceColumns,
    useEmployeeColumns,
    useSalaryPreviewColumns,
} from "../../../components/CreateSalary/CreateSalaryColumns";
import CreateSalaryConfirmModal from "../../../components/CreateSalary/CreateSalaryConfirmModal";
import AddSalaryHeadModal from "../../../components/CreateSalary/AddSalaryHeadModal";
import UploadSalaryHeadConfirmModal from "../../../components/CreateSalary/UploadSalaryHeadConfirmModal";
import LockAttendanceConfirmModal from "../../../components/CreateSalary/LockAttendanceConfirmModal";
import SalaryPreviewUserDetailModal from "../../../components/CreateSalary/SalaryPreviewUserDetailModal";
import EmployeeSalaryBreakupModal from "../../../components/CreateSalary/EmployeeSalaryBreakupModal";
import CreateSalaryTabs from "../../../components/CreateSalary/CreateSalaryTabs";
import { useCreateSalary } from "../../../hooks/CreateSalaryHooks/useCreateSalary";
import type { ISalaryPreviewUser } from "@/interface/attendance.interface";
import type { IUser } from "@/interface/employeeOnBoard";

const CreateSalary = () => {
    const salary = useCreateSalary();
    const [selectedUserForDetail, setSelectedUserForDetail] = useState<ISalaryPreviewUser | null>(null);
    const [selectedEmployeeForSalary, setSelectedEmployeeForSalary] = useState<IUser | null>(null);

    const employeeColumns = useEmployeeColumns(
        salary.paginationData,
        salary.selectedCategory,
        setSelectedEmployeeForSalary,
    );
    const attendanceColumns = useAttendanceColumns(
        salary.attendancePaginationData,
        salary.handleDownloadSalarySlip,
        salary.downloadingSalaryId,
        salary.selectedBranchId,
    );
    const { validUsersColumns, errorUsersColumns } = useSalaryPreviewColumns(
        salary.salaryPreviewPaginationData,
        salary.handleOpenAddSalaryHeadModal,
        salary.handleRemoveSalaryHead,
        salary.salaryPreviewUsers,
        setSelectedUserForDetail,
    );

    const employeeToolbar = useMemo(
        () => (
            <EmployeeToolbar
                selectedEmployeeIds={salary.selectedEmployeeIds}
                selectedCount={salary.selectedEmployeeIds.size}
                selectionType={salary.selectionType}
                isSelectingUsers={salary.isSelectingUsers}
                selectedFile={salary.selectedFile}
                isAllBranchUsersSelected={salary.isAllBranchUsersSelected}
                isDownloading={salary.isDownloading}
                isUploading={salary.isUploading}
                canDownloadTemplate={salary.canDownloadTemplate}
                fileInputRef={salary.fileInputRef}
                onSelectUsersByMode={salary.handleSelectUsersByMode}
                onToggleSelectAllBranchUsers={salary.handleToggleSelectAllBranchUsers}
                onDownloadTemplate={salary.handleDownloadTemplate}
                onFileChange={salary.handleFileChange}
                onClearFile={salary.handleClearFile}
                onUploadFile={salary.handleOpenUploadConfirmModal}
            />
        ),
        [
            salary.selectedEmployeeIds,
            salary.selectionType,
            salary.isSelectingUsers,
            salary.selectedFile,
            salary.isAllBranchUsersSelected,
            salary.isDownloading,
            salary.isUploading,
            salary.canDownloadTemplate,
            salary.fileInputRef,
            salary.handleSelectUsersByMode,
            salary.handleToggleSelectAllBranchUsers,
            salary.handleDownloadTemplate,
            salary.handleFileChange,
            salary.handleClearFile,
            salary.handleOpenUploadConfirmModal,
        ],
    );

    const isPastYear = salary.attendanceStartDate
        ? new Date(salary.attendanceStartDate).getFullYear() < new Date().getFullYear()
        : false;

    const attendanceToolbar = useMemo(
        () => (
            <AttendanceToolbar
                isAllBranchUsersSelected={salary.isAllBranchUsersSelected}
                selectedCount={salary.selectedEmployeeIds.size}
                selectionType={salary.selectionType}
                isSelectingUsers={salary.isSelectingUsers}
                isAttendanceLocked={salary.isAttendanceLocked}
                isLockingAttendance={salary.isLockingAttendance}
                isPastYear={isPastYear}
                onSelectUsersByMode={salary.handleSelectUsersByMode}
                onToggleSelectAllBranchUsers={salary.handleToggleSelectAllBranchUsers}
                onLockAttendance={salary.handleOpenLockAttendanceModal}
            />
        ),
        [
            salary.isAllBranchUsersSelected,
            salary.selectedEmployeeIds.size,
            salary.selectionType,
            salary.isSelectingUsers,
            salary.isAttendanceLocked,
            salary.isLockingAttendance,
            isPastYear,
            salary.handleSelectUsersByMode,
            salary.handleToggleSelectAllBranchUsers,
            salary.handleOpenLockAttendanceModal,
        ],
    );

    const salaryToolbar = useMemo(
        () => (
            <SalaryCreateAction
                salarySubTab={salary.salarySubTab}
                readyUsersCount={salary.salaryPreviewUsers.length}
                isCreatingSalary={salary.isCreatingSalary}
                onOpenConfirm={salary.openCreateSalaryConfirmModal}
            />
        ),
        [
            salary.salarySubTab,
            salary.salaryPreviewUsers.length,
            salary.isCreatingSalary,
            salary.openCreateSalaryConfirmModal,
        ],
    );

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <MainHeading title="Salary Configuration & Creation" isBackButtonVisible={true} />
                    <SelectionSummary
                        selectedEmployeeIds={salary.selectedEmployeeIds}
                        isAllBranchUsersSelected={salary.isAllBranchUsersSelected}
                    />
                </div>

                <CreateSalaryFiltersBar
                    branches={salary.branches}
                    selectedBranchId={salary.selectedBranchId}
                    isLoadingBranches={salary.isLoadingBranches}
                    onBranchSelect={salary.handleBranchSelect}
                    availableSites={salary.availableSites}
                    selectedSiteId={salary.selectedSiteId}
                    isLoadingSites={salary.isLoadingSites}
                    onSiteSelect={salary.handleSiteSelect}
                    selectedCategory={salary.selectedCategory}
                    onCategoryChange={salary.handleCategoryChange}
                />

                <Card className="p-4 sm:p-6 shadow-xs border border-slate-200/80">
                    <CreateSalaryTabs
                        activeTab={salary.activeTab}
                        onTabChange={salary.setActiveTab}
                        searchValue={salary.searchValue}
                        onSearchChange={salary.handleSearchChange}
                        employeeToolbar={employeeToolbar}
                        attendanceToolbar={attendanceToolbar}
                        salaryToolbar={salaryToolbar}
                        employeeColumns={employeeColumns}
                        attendanceColumns={attendanceColumns}
                        validUsersColumns={validUsersColumns}
                        errorUsersColumns={errorUsersColumns}
                        employees={salary.employees}
                        salaryConfigData={salary.salaryConfigData}
                        branchesWiseSalaryHead={salary.branchesWiseSalaryHead}
                        attendanceList={salary.attendanceList}
                        paginatedSalaryData={salary.paginatedSalaryData}
                        salaryPreviewUsers={salary.salaryPreviewUsers}
                        salaryPreviewErrors={salary.salaryPreviewErrors}
                        selectedEmployeeIds={salary.selectedEmployeeIds}
                        isAttendanceLocked={salary.isAttendanceLocked}
                        paginationData={salary.paginationData}
                        salaryConfigPaginationData={salary.salaryConfigPaginationData}
                        attendancePaginationData={salary.attendancePaginationData}
                        salaryPreviewPaginationData={salary.salaryPreviewPaginationData}
                        attendanceStartDate={salary.attendanceStartDate}
                        attendanceEndDate={salary.attendanceEndDate}
                        salarySubTab={salary.salarySubTab}
                        isLoadingEmployees={salary.isLoadingEmployees}
                        isLoadingSalaryConfig={salary.isLoadingSalaryConfig}
                        isLoadingAttendance={salary.isLoadingAttendance}
                        isLoadingSalaryPreview={salary.isLoadingSalaryPreview}
                        setPaginationData={salary.setPaginationData}
                        setSalaryConfigPaginationData={salary.setSalaryConfigPaginationData}
                        setAttendancePaginationData={salary.setAttendancePaginationData}
                        setSalaryPreviewPaginationData={salary.setSalaryPreviewPaginationData}
                        setSalarySubTab={salary.setSalarySubTab}
                        onAttendanceDateRangeChange={salary.handleAttendanceDateRangeChange}
                        onChangePageSize={salary.handleChangePageSize}
                        onSalaryConfigChangePageSize={salary.handleSalaryConfigChangePageSize}
                        onAttendanceChangePageSize={salary.handleAttendanceChangePageSize}
                        onSalaryPreviewChangePageSize={
                            salary.handleSalaryPreviewChangePageSize
                        }
                        onSaveUserSalary={salary.handleSaveUserSalary}
                        onSelectAll={salary.handleSelectAll}
                        onAttendanceSelectAll={salary.handleAttendanceSelectAll}
                        onSelectItem={salary.handleSelectItem}
                        onViewSalaryBreakup={setSelectedEmployeeForSalary as any}
                    />
                </Card>
            </div>

            <CreateSalaryConfirmModal
                show={salary.showCreateSalaryConfirmModal}
                readyUsersCount={salary.salaryPreviewUsers.length}
                attendanceStartDate={salary.attendanceStartDate}
                attendanceEndDate={salary.attendanceEndDate}
                isCreatingSalary={salary.isCreatingSalary}
                onClose={salary.closeCreateSalaryConfirmModal}
                onConfirm={salary.handleConfirmCreateSalary}
            />

            <UploadSalaryHeadConfirmModal
                show={salary.showUploadConfirmModal}
                fileName={salary.selectedFile?.name}
                isUploading={salary.isUploading}
                onClose={salary.handleCloseUploadConfirmModal}
                onConfirm={salary.handleConfirmUploadFile}
            />

            <LockAttendanceConfirmModal
                show={salary.showLockAttendanceModal}
                attendanceStartDate={salary.attendanceStartDate}
                attendanceEndDate={salary.attendanceEndDate}
                selectedCount={salary.selectedEmployeeIds.size}
                isAllSelected={salary.isAllBranchUsersSelected}
                isLocking={salary.isLockingAttendance}
                onClose={salary.handleCloseLockAttendanceModal}
                onConfirm={salary.handleConfirmLockAttendance}
            />

            <AddSalaryHeadModal
                show={salary.showAddSalaryHeadModal}
                onClose={salary.handleCloseAddSalaryHeadModal}
                selectedUser={salary.selectedUserForAddHead}
                branchSalaryHeads={salary.branchesWiseSalaryHead}
                onAddSalaryHead={salary.handleAddSalaryHead}
            />

            <SalaryPreviewUserDetailModal
                show={Boolean(selectedUserForDetail)}
                user={selectedUserForDetail}
                startDate={salary.attendanceStartDate}
                endDate={salary.attendanceEndDate}
                onClose={() => setSelectedUserForDetail(null)}
            />

            <EmployeeSalaryBreakupModal
                show={Boolean(selectedEmployeeForSalary)}
                user={selectedEmployeeForSalary}
                onClose={() => setSelectedEmployeeForSalary(null)}
            />
        </Layout>
    );
};

export default CreateSalary;
